// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import api from "../services/api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // ---------------------------------------------
  // Helper para mapear o payload do token JWT
  // ---------------------------------------------
  const mapDecodedToUser = (decoded) => {
    return {
      // ID DO USUÁRIO (Usuario), que o backend coloca em user_id
      id: decoded.user_id || decoded.id || decoded.sub,
      email: decoded.email || "",
      isAluno: decoded.is_aluno ?? decoded.isAluno ?? false,
      isColaborador: decoded.is_colaborador ?? decoded.isColaborador ?? false,
    };
  };

  const mapApiUserToUser = (apiUser) => {
    return {
      id: apiUser.id,
      email: apiUser.email,
      isAluno: apiUser.is_aluno ?? apiUser.isAluno ?? false,
      isColaborador: apiUser.is_colaborador ?? apiUser.isColaborador ?? false,
    };
  };

  // ---------------------------------------------
  // Re-hidrata usuário a partir do token salvo
  // ---------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("accessToken");

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const mappedUser = mapDecodedToUser(decoded);

        setUser(mappedUser);
        // Garante que o axios já saia autenticado
        api.defaults.headers.Authorization = `Bearer ${token}`;
      } catch (err) {
        console.error("Token inválido ao iniciar:", err);
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        setUser(null);
      }
    }

    setInitializing(false);
  }, []);

  // ---------------------------------------------
  // LOGIN
  // ---------------------------------------------
  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await api.post("/token/", { email, password });
      // backend: { access, refresh, user: { id, email, is_aluno, is_colaborador } }
      const { access, refresh, user: apiUser } = response.data;

      // Salva tokens
      localStorage.setItem("accessToken", access);
      localStorage.setItem("refreshToken", refresh);

      // Configura axios para usar imediatamente o token
      api.defaults.headers.Authorization = `Bearer ${access}`;

      // Se vier o objeto user na resposta, usamos ele.
      // Senão, mapeamos a partir do token.
      let finalUser;
      if (apiUser) {
        finalUser = mapApiUserToUser(apiUser);
      } else {
        const decoded = jwtDecode(access);
        finalUser = mapDecodedToUser(decoded);
      }

      setUser(finalUser);
      return true;
    } catch (error) {
      console.error("Erro no login:", error?.response?.data || error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------
  // LOGOUT
  // ---------------------------------------------
  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    delete api.defaults.headers.Authorization;
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        initializing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
